/* ============================================================
   Boot + frame loop for the product-showcase cards.

   Everything is driven by however many .groovy-pcard elements are in
   the markup — showing a fourth product is a Liquid change, not a JS
   one.

   Models load lazily. A can is ~2MB, and a page should not pay for
   products the shopper has not scrolled to yet, so each card fetches
   its .glb only once it is within a screen of the viewport.

   Differences from the standalone prototype, all forced by Shopify:
     · a section can be rendered more than once on a page, so state is
       per-root rather than per-page
     · the theme editor re-renders a section on every settings change,
       so every instance has to be disposable (see the scene's dispose)
     · one requestAnimationFrame loop drives every instance, rather
       than each booting its own
   ============================================================ */

// Same version query as in assets/groovy-cards-scene.js, deliberately: two
// different ones are two module instances of what is meant to be one table of
// constants, and only one of them would answer to __groovyCards.CARD.
import { CARD } from './groovy-cards-config.js?v=3';
// Versioned for the reason spelled out in groovy-can-experience.js: a
// relative import is served from a URL the CDN pins for days, so an edit to
// the markup layer never reaches the browser on its own. Bump on every
// change to it.
import { buildCards } from './groovy-cards-markup.js?v=1';
// Same reasoning — bump on every change to the scene.
import { createScene } from './groovy-cards-scene.js?v=13';

/** live instances, keyed by their root element */
const instances = new Map();
let rafId = null;
let last = 0;
let start = 0;

/* ---------- one loop for every instance ---------- */

const SAFE_MAX_BUFFER = 4096;

function place(inst) {
  const { canvas, root, scene } = inst;
  const r = root.getBoundingClientRect();
  if (!r.width || !r.height) return;
  const dpr = Math.min(window.devicePixelRatio, 2);
  const fits = Math.max(r.width, r.height) * dpr <= SAFE_MAX_BUFFER;
  canvas.classList.toggle('groovy-cards__canvas--viewport', !fits);
  if (fits) {
    canvas.style.left = `${r.left + window.scrollX}px`;
    canvas.style.top = `${r.top + window.scrollY}px`;
    canvas.style.width = `${r.width}px`;
    canvas.style.height = `${r.height}px`;
  } else {
    for (const prop of ['left','top','width','height']) canvas.style.removeProperty(prop);
  }
  scene?.resize();
}

function nudge(inst) {
  const { canvas, root } = inst;
  if (canvas.classList.contains('groovy-cards__canvas--viewport')) return;
  const grid = root.getBoundingClientRect();
  const box = canvas.getBoundingClientRect();
  const dx = grid.left - box.left, dy = grid.top - box.top;
  if (Math.abs(dx) < 1 && Math.abs(dy) < 1) return;
  canvas.style.left = `${(parseFloat(canvas.style.left) || 0) + dx}px`;
  canvas.style.top = `${(parseFloat(canvas.style.top) || 0) + dy}px`;
}

function frame(now) {
  // Clamped: a backgrounded tab resumes with a huge gap, and an
  // unclamped dt would teleport every can a few revolutions on.
  const dt = Math.min((now - last) / 1000, 0.05);
  last = now;
  const elapsed = (now - start) / 1000;

  for (const inst of instances.values()) {
    nudge(inst);
    inst.scene.measure();       // all layout reads, batched
    inst.scene.update(dt, elapsed);
  }

  rafId = instances.size ? requestAnimationFrame(frame) : null;
}

function startLoop() {
  if (rafId !== null) return;
  last = performance.now();
  if (!start) start = last;
  rafId = requestAnimationFrame(frame);
}

/* ---------- one instance ---------- */

function init(root) {
  if (instances.has(root)) return;

  const cards = buildCards(root);
  if (!cards.length) return;

  /* The canvas goes on <body>, not inside the section. It is
     position:fixed and has to be sized to the viewport, and any
     ancestor with a transform/filter/will-change would silently make
     it that ancestor's child instead — themes grow those wrappers over
     time and the failure looks like a mis-scaled can, not a CSS bug. */
  const canvas = document.createElement('canvas');
  canvas.className = 'groovy-cards__canvas';
  canvas.setAttribute('aria-hidden', 'true');
  document.body.appendChild(canvas);

  const inst = { cards, canvas, root, scene: null, io: null };
  place(inst);

  const scene = createScene(canvas, cards);
  inst.scene = scene;
  place(inst);

  /* A can is ~2MB; nothing is fetched until the card is within a
     screen of the viewport. */
  let io = null;
  if ('IntersectionObserver' in window) {
    io = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const i = cards.findIndex(c => c.el === entry.target);
          if (i >= 0) scene.loadCard(i);
          io.unobserve(entry.target);
        }
      },
      { rootMargin: '100% 0px' }   // a screen of runway before the card arrives
    );
    for (const card of cards) io.observe(card.el);
  } else {
    cards.forEach((_, i) => scene.loadCard(i));
  }

  inst.io = io;
  inst.observer = new ResizeObserver(() => place(inst));
  inst.onResize = () => place(inst);
  instances.set(root, inst);
  inst.observer.observe(root);
  window.addEventListener('resize', inst.onResize);
  startLoop();
}

function destroy(root) {
  const inst = instances.get(root);
  if (!inst) return;

  inst.io?.disconnect();
  inst.observer?.disconnect();
  if (inst.onResize) window.removeEventListener('resize', inst.onResize);
  for (const card of inst.cards) card.teardown();
  inst.scene.dispose();
  inst.canvas.remove();
  instances.delete(root);
}

/* ---------- scanning ----------
   Exposed so callers that swap markup in via innerHTML (an AJAX
   section render, say) can re-run it: browsers never execute <script>
   tags that arrive that way, so freshly inserted cards would otherwise
   stay unbound. Roots that have gone from the document are disposed on
   the same pass. */

function refresh(scope = document) {
  for (const root of instances.keys()) {
    if (!root.isConnected) destroy(root);
  }
  scope.querySelectorAll('[data-groovy-cards]').forEach(init);
}

window.groovyCardsRefresh = refresh;
// live handle for tuning in the console: __groovyCards.CARD.turns = 2
window.__groovyCards = { CARD, instances, refresh };

refresh();

/* ---------- theme editor ----------
   Section load/unload fire on the .shopify-section wrapper. Without the
   unload half, a merchant nudging a slider leaks one WebGL context per
   re-render and the browser starts dropping the oldest at ~16. */

document.addEventListener('shopify:section:load', e => refresh(e.target));
document.addEventListener('shopify:section:unload', e => {
  e.target.querySelectorAll('[data-groovy-cards]').forEach(destroy);
});
